import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
/** Components */
import { FormSuccess } from 'Components/Form/Success';
import { PageHelper } from 'Components/PageHelper';
import { StepperForm } from 'Components/StepperForm';
import { Container } from '../../../Components/Container';
import { Detalhes } from './Details';
import { Vinculos } from './Bonds';
import { Row } from 'Components/Grid';
import { TitleForm } from 'Components/Form/styles';
/** Services */
import { addSystem, editSystem, getSystemById } from 'services/systems-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useQuery } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';

export const SystemsForm = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const steps = [t('mapping:systems.form.detailsStep'), t('mapping:systems.form.bondsStep')];
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [idSystem, setIdSystem] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      user: '',
      tags: [],
      pii: false,
      saas: false,
      developedInHouse: false,
      dataBase: '',
      description: '',
      areas: [],
      processes: [],
      assets: [],
      companyKey: null,
    },
  });

  const totalSteps = () => {
    return steps.length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const handleNext = () => {
    const newActiveStep = isLastStep() ? activeStep : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const formatArea = area => {
    return {
      _id: area._id,
      name: area.name,
      description: area.description,
      user: area.user,
      criticity: area.criticity,
    };
  };

  const formatAsset = asset => {
    return {
      _id: asset._id,
      name: asset.name,
      description: asset.description,
      user: asset.user,
      criticity: asset.criticity,
    };
  };

  const formatProcess = process => {
    return {
      _id: process._id,
      name: process.name,
      description: process.description,
      user: process.user,
      criticity: process.criticity,
    };
  };

  const formatForm = dados => {
    return {
      name: dados.name,
      criticity: dados.criticity,
      user: dados.user,
      tags: dados.tags.length > 0 ? dados.tags : [],
      pii: dados.pii ?? false,
      saas: dados.saas ?? false,
      developedInHouse: dados.developedInHouse ?? false,
      dataBase: dados.dataBase ?? '',
      description: dados.description ?? '',
      areas: dados.areas.length > 0 ? dados.areas.map(area => formatArea(area)) : [],
      processes: dados.processes.length > 0 ? dados.processes.map(process => formatProcess(process)) : [],
      assets: dados.assets.length > 0 ? dados.assets.map(asset => formatAsset(asset)) : [],
      companyKey: dados.companyKey ?? selectedTenant.slug,
    };
  };

  const handleComplete = async () => {
    try {
      setIsLoadingForm(true);
      const formattedForm = formatForm(getValues());
      const res = id ? await editSystem(id, formattedForm) : await addSystem(formattedForm);
      if (res.status === 200 || res.status === 201) {
        setIdSystem(res.data._id ?? id);
        setFinished(true);
        setIsLoadingForm(false);
      } else {
        throw new Error(t('mapping:systems.form.errorSave'));
      }
    } catch (error) {
      setIsLoadingForm(false);
      SwalReact.fire({
        title: t('mapping:systems.toasts.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setActiveStep(0);
    setFinished(false);
    setIdSystem(null);
    navigate('/systems/add');
  };

  const getTitleStep = () => {
    if (finished) return <></>;

    if (activeStep === 0)
      return (
        <Row>
          <TitleForm>{t('mapping:systems.form.detailsTitle')}</TitleForm>
        </Row>
      );

    return (
      <Row>
        <TitleForm>{t('mapping:systems.form.bondsTitle')}</TitleForm>
      </Row>
    );
  };

  const {
    isLoading,
    error,
    data: system,
  } = useQuery(['getSystem', id], async () => {
    if (!id) return null;

    const resSystem = await getSystemById(id);

    if (!resSystem.data) return null;

    return resSystem.data;
  });

  useEffect(() => {
    if (!id) return;
    if (!system) return;

    Object.keys(system).forEach(key => {
      setValue(key, system[key]);
    });

    return () => {};
  }, [id, system, setValue]);

  const showForm = () => {
    if (id) return !isLoading && !error;
    return true;
  };

  return (
    <Container title={id ? t('mapping:systems.form.editTitle') : t('mapping:systems.form.addTitle')} breadcrumb={t('mapping:systems.list.breadcrumb')} linkPage="/systems" dataTour="page-title">
      {isLoading && <PageLoaderForm />}
      {showForm() && (
        <>
          <div data-tour="stepper">
            <StepperForm activeStep={activeStep} steps={steps} />
          </div>
          <div className="card-content">
            {getTitleStep()}
            {activeStep === 0 && (
              <Detalhes
                control={control}
                register={register}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
                errors={errors}
                getValues={getValues}
                id={id}
              />
            )}
            {activeStep === 1 && !finished && (
              <Vinculos
                control={control}
                register={register}
                handleBack={handleBack}
                handleComplete={handleComplete}
                handleSubmit={handleSubmit}
                isLoading={isLoadingForm}
              />
            )}
            {finished && (
              <FormSuccess
                title={t('mapping:systems.form.successMessage')}
                labelStartAgain={t('mapping:systems.form.addAnother')}
                handleStartAgain={() => handleReset()}
                labelShow={t('mapping:systems.form.viewDetails')}
                handleShow={() => navigate('/systems/show/' + idSystem)}
              />
            )}
          </div>
        </>
      )}
      {!finished && <PageHelper steps={getTourSteps(t)} tourKey="systems-form" />}
    </Container>
  );
};

const getTourSteps = (t) => [
  { target: 'body', content: t('mapping:systems.tour.form.welcome'), placement: 'center', disableBeacon: true },
  { target: '[data-tour="page-title"]', content: t('mapping:systems.tour.form.pageTitle'), disableScrolling: true },
  { target: '[data-tour="stepper"]', content: t('mapping:systems.tour.form.stepper'), disableScrolling: true },
  { target: '[data-tour="system-name"]', content: t('mapping:systems.tour.form.systemName'), disableScrolling: true },
  { target: '[data-tour="system-criticity"]', content: t('mapping:systems.tour.form.systemCriticity'), disableScrolling: true },
  { target: '[data-tour="system-user"]', content: t('mapping:systems.tour.form.systemUser'), disableScrolling: true },
  { target: '[data-tour="system-tags"]', content: t('mapping:systems.tour.form.systemTags'), disableScrolling: true },
  { target: '[data-tour="system-database"]', content: t('mapping:systems.tour.form.systemDatabase'), disableScrolling: true },
  { target: '[data-tour="system-checkboxes"]', content: t('mapping:systems.tour.form.systemCheckboxes') },
  { target: '[data-tour="system-description"]', content: t('mapping:systems.tour.form.systemDescription') },
  { target: '[data-tour="form-actions"]', content: t('mapping:systems.tour.form.formActions') },
  { target: 'body', content: t('mapping:systems.tour.form.done'), placement: 'center' },
];
